import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Webhook, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Eye,
  Clock,
  Server,
  ChevronDown,
  ChevronUp,
  Copy
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebhookLogsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/webhook-logs?limit=100`, {
        headers: getAuthHeaders()
      });
      setLogs(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch webhook logs:', error);
      toast.error('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  };

  const viewLogDetail = async (logId) => {
    try {
      setDetailLoading(true);
      setDetailDialogOpen(true);
      const response = await axios.get(`${API}/admin/webhook-logs/${logId}`, {
        headers: getAuthHeaders()
      });
      setSelectedLog(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch log detail:', error);
      toast.error('Failed to load log details');
    } finally {
      setDetailLoading(false);
    }
  };

  const copyPayload = () => {
    if (selectedLog?.raw_payload) {
      navigator.clipboard.writeText(JSON.stringify(selectedLog.raw_payload, null, 2));
      toast.success('Payload copied to clipboard');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      SUCCESS: { class: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
      FAILED: { class: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
      DUPLICATE: { class: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertCircle },
      PROCESSING: { class: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock }
    };
    const statusConfig = config[status] || config.PROCESSING;
    const Icon = statusConfig.icon;
    return (
      <Badge className={`${statusConfig.class} border font-medium flex items-center gap-1 px-2 py-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const stats = {
    total: logs.length,
    success: logs.filter(l => l.processing_status === 'SUCCESS').length,
    failed: logs.filter(l => l.processing_status === 'FAILED').length,
    duplicate: logs.filter(l => l.processing_status === 'DUPLICATE').length
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <Webhook className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-700">Admin Access Required</h2>
          <p className="text-slate-500 text-sm mt-2">Only administrators can view webhook logs.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="webhook-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950 flex items-center gap-3">
            <Webhook className="w-7 h-7" />
            Webhook Logs
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Monitor incoming Zoho Forms webhook requests
          </p>
        </div>
        <Button onClick={fetchLogs} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Requests</p>
                <p className="text-2xl font-bold font-mono text-indigo-950">{stats.total}</p>
              </div>
              <Server className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Successful</p>
                <p className="text-2xl font-bold font-mono text-emerald-700">{stats.success}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Failed</p>
                <p className="text-2xl font-bold font-mono text-red-700">{stats.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Duplicates</p>
                <p className="text-2xl font-bold font-mono text-amber-700">{stats.duplicate}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
            </div>
          ) : logs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Received At</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Source IP</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Expense ID</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Error</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr 
                      key={log.id} 
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`webhook-log-${index}`}
                    >
                      <td className="py-3 px-4 font-mono text-sm text-slate-600">
                        {formatDate(log.received_at)}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(log.processing_status)}
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-slate-500">
                        {log.source_ip || '-'}
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-slate-600">
                        {log.expense_id ? log.expense_id.slice(0, 8) + '...' : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-red-600 max-w-[200px] truncate">
                        {log.error_message || '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewLogDetail(log.id)}
                          data-testid={`view-log-${index}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 text-center">
              <Webhook className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No webhook logs yet</h3>
              <p className="text-slate-500 text-sm">Logs will appear here when Zoho Forms sends data.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-indigo-950">Webhook Log Details</DialogTitle>
            <DialogDescription>
              Full details of the webhook request
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
            </div>
          ) : selectedLog ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Received At</p>
                  <p className="font-mono text-sm">{formatDate(selectedLog.received_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Status</p>
                  {getStatusBadge(selectedLog.processing_status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Source IP</p>
                  <p className="font-mono text-sm">{selectedLog.source_ip || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Expense ID</p>
                  <p className="font-mono text-sm">{selectedLog.expense_id || '-'}</p>
                </div>
              </div>
              
              {selectedLog.error_message && (
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 uppercase font-semibold mb-1">Error Message</p>
                  <p className="text-sm text-red-700">{selectedLog.error_message}</p>
                </div>
              )}
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">Raw Payload</p>
                  <Button variant="outline" size="sm" onClick={copyPayload}>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-[300px] overflow-y-auto">
                  {JSON.stringify(selectedLog.raw_payload, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhookLogsPage;
